import { AuditSession } from "../../../../models/audit/auditSession";

// Configuration for mapping issue types to display statuses
// Edit the arrays below to control which issues trigger each status
interface IssueStatusConfig {
  na: {
    categories: string[];
    subcategories: string[];
  };
  requiresAttention: {
    categories: string[];
    subcategories: string[];
  };
  issuesFound: {
    categories: string[];
    subcategories: string[];
  };
}

export const ISSUE_STATUS_CONFIG: IssueStatusConfig = {
  // Issues that should display as "N/A" with grey styling
  na: {
    categories: [],
    subcategories: ["not_applicable"],
  },
  // Issues that should display as "Requires Attention" with orange styling
  requiresAttention: {
    categories: [],
    subcategories: ["exhausted_search", "inconclusive"],
  },
  // Issues that should display as "Issues Found" with red styling
  issuesFound: {
    categories: ["test_outcome", "anomaly", "exception"],
    subcategories: [
      "issues_found",
      "insufficient_evidence",
      "inconclusive_evidence",
    ],
  },
};

export type IssueDisplayStatus =
  | "na"
  | "requires_attention"
  | "issues_found"
  | "no_issues";

// Helper function to determine the display status based on issues array
export function getIssueDisplayStatus(
  issues: AuditSession["sourceRows"][number]["checkResults"][number]["issues"],
): IssueDisplayStatus {
  // If no issues, return "no_issues"
  if (!issues || issues.length === 0) {
    return "no_issues";
  }

  const hasNoIssuesFound = issues.some(
    (issue) =>
      issue.issueCategory === "test_outcome" &&
      issue.issueSubcategory === "no_issues_found",
  );
  if (hasNoIssuesFound) {
    return "no_issues";
  }

  // Check for "N/A" issues first (highest priority)
  const hasNAIssue = issues.some(
    (issue) =>
      ISSUE_STATUS_CONFIG.na.categories.includes(issue.issueCategory) ||
      ISSUE_STATUS_CONFIG.na.subcategories.includes(issue.issueSubcategory),
  );
  if (hasNAIssue) {
    return "na";
  }

  // Check for "Requires Attention" issues
  const hasRequiresAttentionIssue = issues.some(
    (issue) =>
      ISSUE_STATUS_CONFIG.requiresAttention.categories.includes(
        issue.issueCategory,
      ) ||
      ISSUE_STATUS_CONFIG.requiresAttention.subcategories.includes(
        issue.issueSubcategory,
      ),
  );
  if (hasRequiresAttentionIssue) {
    return "requires_attention";
  }

  // Check for "Issues Found" issues
  const hasIssuesFound = issues.some(
    (issue) =>
      ISSUE_STATUS_CONFIG.issuesFound.categories.includes(
        issue.issueCategory,
      ) ||
      ISSUE_STATUS_CONFIG.issuesFound.subcategories.includes(
        issue.issueSubcategory,
      ),
  );
  if (hasIssuesFound) {
    return "issues_found";
  }

  // Default to "no_issues" if no configured issues match
  return "no_issues";
}
